/**
 * Wipes everything this console keeps in the browser, for sign-out: nothing of
 * the account — tokens, cached lists, preferences, report filters, Sentry
 * replay state — may be left for the next person at the same browser.
 *
 * JavaScript clears what it can reach: localStorage, sessionStorage, the
 * cookies of this host, Cache Storage, IndexedDB and service workers. What it
 * cannot reach — the HTTP cache and HttpOnly cookies — is cleared by the
 * browser itself when a same-origin response carries `Clear-Site-Data`
 * (nginx serves it at CLEAR_SITE_DATA_PATH; see nginx.conf).
 */

export const CLEAR_SITE_DATA_PATH = '/__clear-site-data'

function quietly(action: () => void) {
  try {
    action()
  } catch {
    /* storage unavailable (private mode, blocked site data): nothing to clear */
  }
}

/**
 * Expires every cookie visible to this page, on this host only — never on the
 * parent domain (.udevs.io), whose cookies belong to other company services.
 */
function clearCookies() {
  const host = window.location.hostname
  const domains = ['', host, `.${host}`]
  const paths = ['/', window.location.pathname]
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0]?.trim()
    if (!name) continue
    for (const domain of domains) {
      for (const path of paths) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=${path}${domain ? `; domain=${domain}` : ''}`
      }
    }
  }
}

async function clearAsyncStores() {
  const jobs: Promise<unknown>[] = []
  if ('caches' in window) jobs.push(caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key)))))
  if ('indexedDB' in window && typeof indexedDB.databases === 'function') {
    jobs.push(
      indexedDB.databases().then((databases) =>
        Promise.all(
          databases
            .filter((database): database is IDBDatabaseInfo & { name: string } => Boolean(database.name))
            .map(
              (database) =>
                new Promise<void>((resolve) => {
                  const request = indexedDB.deleteDatabase(database.name)
                  request.onsuccess = request.onerror = request.onblocked = () => resolve()
                }),
            ),
        ),
      ),
    )
  }
  if ('serviceWorker' in navigator) {
    jobs.push(navigator.serviceWorker.getRegistrations().then((registrations) => Promise.all(registrations.map((registration) => registration.unregister()))))
  }
  await Promise.allSettled(jobs)
}

/** Clears all client-side data of this origin. Never throws; each part is best effort. */
export async function clearClientData() {
  quietly(() => localStorage.clear())
  quietly(() => sessionStorage.clear())
  quietly(clearCookies)
  await clearAsyncStores()
  // The HTTP cache and HttpOnly cookies: only the browser can drop them, on this header.
  await fetch(CLEAR_SITE_DATA_PATH, { method: 'POST', cache: 'no-store', credentials: 'same-origin' }).catch(() => undefined)
}
